import type { MouseEvent } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import type { Reference } from 'sr-types/lib/production/v1/graphql';
import type { Note } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../../../common/auth/UserContext';
import { FORM_WIDGET_COMPONENTS } from '../../../common/form/formConstants';
import { FormProvider } from '../../../common/form/FormContext';
import FormWidget from '../../../common/form/FormWidget';
import I18n from '../../../common/i18n/I18n';
import LexicalRendererFooter from '../../../common/lexicalRenderer/LexicalRendererFooter';
import { getNoteOwner, useSaveNote } from '../../../common/notes';
import { deriveNoteTitleFromContent } from '../../../common/notes/utils';
import EmptyPromise from '../../../common/utils/EmptyPromise';
import { getNoteInputForProduction } from '../../helpers/productionNote';

export default function ProductionStatusNoteRenderer({
    note,
    isEditing,
    toggleEditor,
    productionReference,
    onStatusNoteChanged
}: {
    note: Note;
    isEditing: boolean;
    toggleEditor: (state: boolean) => void;
    productionReference: Reference;
    onStatusNoteChanged?: (note: Note | null, productionId: string) => void;
}) {
    const { userProfile } = useContext(UserContext);
    const { saveNewNote } = useSaveNote({ showMessage: true });

    const [noteValues, setNoteValues] = useState(null);

    useEffect(() => {
        if (note?.reference?.id || note?.id) {
            setNoteValues(getNoteInputForProduction(note, productionReference));
        }
    }, [productionReference, note]);

    const onSaveNote = (title: string, _htmlString: string, event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        toggleEditor(false);

        if (!noteValues) {
            return EmptyPromise;
        }

        const noteOwner = getNoteOwner(note?.updatedByName, userProfile?.name);

        // Create a sanitized copy to avoid state mutation and runtime errors.
        const sanitizedNoteValues = {
            ...noteValues,
            title: title || deriveNoteTitleFromContent(noteValues?.content)
        };

        onStatusNoteChanged?.(
            {
                ...sanitizedNoteValues,
                id: sanitizedNoteValues?.identity?.id,
                reference: note.reference || { id: sanitizedNoteValues?.identity?.id, type: 'note' },
                security: note.security,
                updatedByName: noteOwner,
                updatedOn: note.updatedOn
            },
            productionReference.id
        );

        return saveNewNote({ input: sanitizedNoteValues })
            .then((id: string) => !id && handleFallbackToPreviousState())
            .catch(() => handleFallbackToPreviousState());
    };

    const handleFallbackToPreviousState = () => {
        onStatusNoteChanged?.(note, productionReference.id);
        setNoteValues(getNoteInputForProduction(note, productionReference));
        toggleEditor(true);
    };

    const onCancelNote = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        toggleEditor(false);
        return EmptyPromise;
    };

    return (
        <FormProvider state={noteValues} setState={setNoteValues}>
            <FormWidget
                name="content"
                label={<I18n token="common.note" />}
                component={FORM_WIDGET_COMPONENTS.richText}
                contentPlaceholder={<I18n token="resource.notes.form.content.placeholder" />}
                onSave={onSaveNote}
                onCancel={onCancelNote}
                sx={{
                    border: 'none',
                    '&:hover': {
                        backgroundColor: 'transparent'
                    },
                    minHeight: isEditing ? 200 : 65,
                    p: 0,
                    flex: 1,
                    gap: 1,
                    borderRadius: 0
                }}
                footer={
                    !isEditing && (
                        <LexicalRendererFooter
                            updatedOn={note.updatedOn}
                            updatedByName={note.updatedByName}
                            content={noteValues?.content ?? note?.content}
                            onTranslated={(translatedText) => {
                                setNoteValues((prevNoteValues) => {
                                    const nextNoteValues =
                                        prevNoteValues ?? getNoteInputForProduction(note, productionReference);
                                    return { ...nextNoteValues, content: translatedText };
                                });
                            }}
                            typographySx={{
                                fontSize: 'caption.fontSize',
                                fontWeight: 'bold'
                            }}
                            sx={{ m: 0 }}
                        />
                    )
                }
                readOnly={!isEditing}
                isEditing={isEditing}
                disableTranslation={true}
            />
        </FormProvider>
    );
}
